import axios from "axios";
import StorageFactory from "../Util/StorageFactory";
import { SESSION_USER_SUBSCRIPTION_STATUS } from "../Util/VMConstants";
import {
  API_ASSIGN_LICENSES_TIER,
  API_CHECK_SUBSCRIPTION_STATUS_TIER,
  API_CHECK_TEAM_USER_LIMIT_TIER,
  API_CREATE_ORDER_TIER,
  API_SUBSCRIPTION_DETAILS_TIER,
  API_SUBSCRIPTION_PLANS_TIER,
  API_TEAM_REGISTRATION_V2,
  API_VERIFY_PAYMENT_TIER,
} from "../Util/URLConstants";

const sessionFactory = new StorageFactory("session");

const jsonHeaders = { "Content-Type": "application/json" };

/** Normalize thunk arg — string user id or `{ userID, forceRefresh }`. */
export function parseSubscriptionFetchArg(arg) {
  if (typeof arg === "string") {
    return { userID: arg, forceRefresh: false };
  }
  return {
    userID: arg?.userID ?? arg?.user_id ?? "",
    forceRefresh: Boolean(arg?.forceRefresh),
  };
}

export const getSubscriptionStatusTierCached = (user_id) => {
  const cached = sessionFactory.get(SESSION_USER_SUBSCRIPTION_STATUS);
  if (!cached || cached.user_id !== user_id) return null;
  return cached.data;
};

export const setSubscriptionStatusTierCached = (user_id, data) => {
  sessionFactory.add(SESSION_USER_SUBSCRIPTION_STATUS, { user_id, data });
};

export const clearSubscriptionStatusTierCache = () => {
  sessionFactory.remove(SESSION_USER_SUBSCRIPTION_STATUS);
};

export const listSubscriptionPlansTier = async ({ user_id, country, currency } = {}) => {
  const params = {};
  if (user_id) params.user_id = user_id;
  if (country) params.country = country;
  if (currency) params.currency = currency;

  const response = await axios.get(API_SUBSCRIPTION_PLANS_TIER, { params });
  return response.data;
};

export const createOrderTier = async ({
  user_id,
  plan_tier,
  plan_name,
  country = "IN",
  contact,
  name,
  email,
  autopay = true,
  recurring,
  apply_gst,
  gst_percent,
  base_amount,
  gst_amount,
  total_amount,
  amount,
}) => {
  const payload = {
    user_id,
    plan_tier,
    plan_name,
    country,
    contact,
    name,
    email,
    autopay,
  };

  if (recurring != null) payload.recurring = recurring;
  else if (autopay) payload.recurring = true;

  if (apply_gst != null) payload.apply_gst = apply_gst;
  if (gst_percent != null) payload.gst_percent = gst_percent;
  if (base_amount != null) payload.base_amount = base_amount;
  if (gst_amount != null) payload.gst_amount = gst_amount;
  if (total_amount != null) payload.total_amount = total_amount;
  if (amount != null) payload.amount = amount;

  const response = await axios.post(API_CREATE_ORDER_TIER, payload, {
    headers: jsonHeaders,
  });
  return response.data;
};

export const verifyPaymentTier = async (payload) => {
  const response = await axios.post(API_VERIFY_PAYMENT_TIER, payload, {
    headers: jsonHeaders,
  });
  return response.data;
};

export const getSubscriptionDetailsTier = async (user_id) => {
  const response = await axios.get(API_SUBSCRIPTION_DETAILS_TIER, {
    params: { user_id },
  });
  return response.data;
};

export const checkTeamUserLimitTier = async (user_id) => {
  const response = await axios.get(API_CHECK_TEAM_USER_LIMIT_TIER, {
    params: { user_id },
  });
  return response.data;
};

export const assignLicensesTier = async (user_id) => {
  const response = await axios.post(
    API_ASSIGN_LICENSES_TIER,
    { user_id },
    { headers: jsonHeaders }
  );
  return response.data;
};

export const checkUserSubscriptionStatusTier = async (user_id) => {
  const response = await axios.get(API_CHECK_SUBSCRIPTION_STATUS_TIER, {
    params: { user_id },
    // Body uses status_code 404; HTTP may still be 400 — do not throw before parsing JSON
    validateStatus: () => true,
  });
  return response.data;
};

/** First call hits API and caches; later calls in same session read session storage. */
export const getUserSubscriptionStatusTier = async (
  user_id,
  { forceRefresh = false } = {}
) => {
  if (!forceRefresh) {
    const cached = getSubscriptionStatusTierCached(user_id);
    if (cached) return cached;
  }

  const data = await checkUserSubscriptionStatusTier(user_id);
  if (data?.status_code === 200) {
    setSubscriptionStatusTierCached(user_id, data);
  }
  return data;
};

export const addTeamRegistrationV2 = async (payload) => {
  const response = await axios.post(API_TEAM_REGISTRATION_V2, payload, {
    headers: jsonHeaders,
  });
  return response.data;
};
